import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES } from '@bre-ticketing/contracts';
import type { ApiError, PendingLoginRow } from '../api';
import { endpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';

/**
 * Addresses that signed in to Admin or the box office (Google or an emailed
 * link, D-370) but have no staff account.
 *
 * Ticketing never auto-provisions: proving an email address is not by itself
 * permission to do anything here, so someone has to grant the first role
 * deliberately (docs/auth.md §4.1).
 */
export default function PendingPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Record<string, string[]>>({});

  const pending = useQuery<{ pending: PendingLoginRow[] }, ApiError>({
    queryKey: ['pending-logins'],
    queryFn: endpoints.pendingLogins,
  });

  // Not everyone in this list should get an account: a typo, somebody else's
  // Google account, a person who has left. Dismissing forgets the row, not the
  // person — if they sign in again it comes back (D-109).
  const dismiss = useMutation({
    mutationFn: (email: string) => endpoints.dismissPending(email),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['pending-logins'] }),
  });

  const promote = useMutation({
    mutationFn: ({ email, roles }: { email: string; roles: string[] }) => endpoints.promote(email, roles),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['pending-logins'] });
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (!can('staff.view')) return <NotPermitted permission="staff.view" />;

  const rows = pending.data?.pending ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Panel
        title="Pending logins"
        description="Proved their email address with Google or an emailed link, but are not staff here yet. Granting a role is what lets them in; they then sign in with that address."
      >
        <Table headers={['Email', 'Attempts', 'Last seen', 'Grant roles', '']}>
          {pending.isPending ? (
            <EmptyRow columns={5}>Loading…</EmptyRow>
          ) : rows.length === 0 ? (
            <EmptyRow columns={5}>Nobody is waiting. An address appears here after it tries to sign in.</EmptyRow>
          ) : (
            rows.map((row) => {
              const roles = chosen[row.email] ?? [];
              return (
                <tr key={row.email} className="border-b border-line last:border-0 align-top">
                  <td className="px-5 py-3">
                    <div className="font-medium text-ink-900">{row.email}</div>
                    <div className="text-xs text-ink-400">
                      {row.name_snapshot ? `${row.name_snapshot} · ` : ''}
                      {row.auth_method === 'GOOGLE_OIDC' ? 'Google' : 'emailed link'} on {row.surface === 'BOX_OFFICE' ? 'the box office' : 'Admin'}
                    </div>
                  </td>
                  <td className="tabular px-5 py-3 text-sm text-ink-600">{row.attempt_count}</td>
                  <td className="tabular px-5 py-3 text-xs text-ink-400">{formatWhen(row.last_seen_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {ROLES.map((role) => (
                        <label key={role} className="flex items-center gap-1.5 text-sm text-ink-900">
                          <input
                            type="checkbox"
                            checked={roles.includes(role)}
                            onChange={(event) =>
                              setChosen((current) => ({
                                ...current,
                                [row.email]: event.target.checked
                                  ? [...roles, role]
                                  : roles.filter((value) => value !== role),
                              }))
                            }
                          />
                          {role.replaceAll('_', ' ')}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {can('staff.manage') ? (
                      <span className="flex justify-end gap-2">
                        <Button
                          variant="primary"
                          disabled={roles.length === 0 || promote.isPending}
                          onClick={() => promote.mutate({ email: row.email, roles })}
                        >
                          Grant access
                        </Button>
                        <Button
                          disabled={dismiss.isPending}
                          onClick={() => {
                            if (window.confirm(`Dismiss ${row.email}? They keep no access either way, and signing in again puts them back on this list.`)) dismiss.mutate(row.email);
                          }}
                        >
                          Dismiss
                        </Button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })
          )}
        </Table>
      </Panel>
    </div>
  );
}
